import { useId, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, Flag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtErr } from "@/lib/api";
import { Icon, Button, IconButton, TextField, Select, Badge, Panel, PageHead, EmptyState } from "@/sentinel";

const STATUS = ["Planned", "In progress", "Done"];
const STATUS_OPTS = STATUS.map((s) => ({ value: s, label: s }));
const STATUS_ICON = { Planned: "Circle", "In progress": "CircleDot", Done: "CircleCheck" };

/** Multi-line field in the TextField style (the library has no textarea). */
function TextArea({ label, rows = 3, ...rest }) {
  const id = useId();
  return (
    <div className="sn-field">
      {label && <label className="sn-field-label" htmlFor={id}>{label}</label>}
      <div className="sn-input-wrap" style={{ height: "auto", alignItems: "stretch" }}>
        <textarea id={id} rows={rows} className="sn-input" style={{ padding: "var(--space-2)", lineHeight: "20px", resize: "vertical" }} {...rest} />
      </div>
    </div>
  );
}

const EMPTY_FORM = { title: "", description: "", target_date: "", status: "Planned" };

export default function Milestones() {
  const { can } = useAuth();
  const canEdit = can("analyst");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const { data: rows = [], refetch } = useQuery({ queryKey: ["pm-milestones"], queryFn: api.pmMilestones });

  const add = async () => {
    if (!form.title.trim()) { toast.error("Title required."); return; }
    try { await api.pmAddMilestone(form); setForm(EMPTY_FORM); setAdding(false); refetch(); toast.success("Milestone added."); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  const del = async (id) => { try { await api.pmDeleteMilestone(id); refetch(); } catch (e) { toast.error(e.message); } };
  const setStatus = async (m, status) => { try { await api.pmUpdateMilestone(m.id, { title: m.title, description: m.description, target_date: m.target_date, status }); refetch(); } catch (e) { toast.error(e.message); } };

  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="pm-milestones">
      <PageHead
        crumb={["Project", "Roadmap"]}
        title="Roadmap"
        context="Phases, with progress from linked tasks."
        actions={canEdit && <Button variant="primary" icon="Plus" aria-expanded={adding} onClick={() => setAdding((v) => !v)} data-testid="pm-milestone-add-btn">Add milestone</Button>}
      />

      {adding && (
        <Panel title="New milestone" icon={Flag} data-testid="pm-milestone-form">
          <div className="sn-stack">
            <TextField label="Title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <TextArea label="Description" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
            <div className="sn-row" style={{ alignItems: "flex-end", gap: "var(--space-3)" }}>
              <TextField label="Target date" type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} />
              <Select label="Status" options={STATUS_OPTS} value={form.status} onChange={(v) => setForm({ ...form, status: v })} width={150} />
              <Button variant="primary" onClick={add}>Add</Button>
            </div>
          </div>
        </Panel>
      )}

      {rows.length === 0 ? (
        <EmptyState icon="Map" title="No milestones yet">Add a phase to start the roadmap.</EmptyState>
      ) : (
        <Panel title="Phases" icon="Map" flush meta={<span className="sn-sans-num">{rows.filter((m) => m.status === "Done").length} of {rows.length} done</span>}>
          {rows.map((m) => (
            <article key={m.id} className="sn-dlog" data-testid={`pm-milestone-${m.id}`}>
              <span className="sn-dlog-dot"><Icon name={Flag} size={14} /></span>
              <div className="sn-row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                <div className="sn-stack" style={{ gap: "var(--space-1)", minWidth: 0, flex: 1 }}>
                  <h3 className="sn-dlog-t">{m.title}</h3>
                  <div className="sn-dlog-meta">
                    <Badge icon={STATUS_ICON[m.status]}>{m.status}</Badge>
                    {m.target_date && <span>due <span className="sn-num">{m.target_date}</span></span>}
                  </div>
                </div>
                {canEdit && (
                  <div className="sn-row" style={{ gap: "var(--space-1)", flex: "none" }}>
                    <Select options={STATUS_OPTS} value={m.status} onChange={(v) => setStatus(m, v)} width={132} aria-label={`Status of ${m.title}`} />
                    <IconButton icon={Trash2} label={`Delete ${m.title}`} size="sm" onClick={() => del(m.id)} />
                  </div>
                )}
              </div>
              {m.description && <p className="sn-dlog-b">{m.description}</p>}
              <div className="sn-stack" style={{ gridColumn: 2, gap: "var(--space-1)" }}>
                <div className="sn-row sn-small sn-faint" style={{ justifyContent: "space-between" }}>
                  <span className="sn-sans-num">{m.done_count}/{m.task_count} tasks done</span><span className="sn-num">{m.progress}%</span>
                </div>
                <div role="img" aria-label={`${m.progress}% complete`}
                  style={{ position: "relative", height: 6, borderRadius: "var(--radius-xs)", background: "var(--border-hairline)", overflow: "hidden" }}>
                  <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${m.progress}%`, background: "var(--viz-seq-4)" }} />
                </div>
              </div>
            </article>
          ))}
        </Panel>
      )}
    </div>
  );
}
